import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { CaretDown, ListBullets, Star, X } from "@phosphor-icons/react";
import { STAFF_SECTIONS, flattenItems, MAX_FAVOURITES } from "../navSections";

/** Settings → Menu Layout: the signed-in user's own sidebar preferences (saved to their account). */
export default function MenuLayoutSettings() {
  const { user, patchUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const collapsible = user?.ui_prefs?.nav_collapsible !== false;
  const favourites = Array.isArray(user?.ui_prefs?.nav_favourites) ? user.ui_prefs.nav_favourites : [];
  const allItems = STAFF_SECTIONS.flatMap((s) => flattenItems(s.items));
  const favItems = favourites.map((to) => allItems.find((i) => i.to === to) || { to, label: to }).filter(Boolean);

  const save = async (value) => {
    setSaving(true);
    try {
      const { data } = await API.put("/users/me/ui-prefs", { nav_collapsible: value });
      patchUser({ ui_prefs: data.ui_prefs });
      toast.success(value ? "Menu groups now collapse" : "Menu groups now stay open");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const unpin = async (to) => {
    try {
      const { data } = await API.put("/users/me/ui-prefs", { nav_favourites: favourites.filter((x) => x !== to) });
      patchUser({ ui_prefs: data.ui_prefs });
      toast.success("Unpinned from Favourites");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetRemembered = () => {
    try { localStorage.removeItem(`necos_nav_open:${user?._id || user?.id || "anon"}`); } catch {}
    toast.success("Menu groups reset — only the group you're in will open");
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="menu-layout-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Menu layout</h3>
        <p className="text-xs text-muted-foreground">How the left-hand menu behaves for you. This is your own setting — it follows your account on every device.</p>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
        <div className="flex gap-3">
          <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><CaretDown size={18} /></div>
          <div>
            <p className="text-sm font-semibold">Collapsible menu groups</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Groups such as Clients, Suppliers and Health &amp; Safety fold up with a click. They start closed except the one holding the page you're on, and remember how you left them.
            </p>
          </div>
        </div>
        <Switch checked={collapsible} disabled={saving} onCheckedChange={save} aria-label="Collapsible menu groups" data-testid="menu-collapsible-switch" />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4 opacity-100">
        <div className="flex gap-3">
          <div className="h-9 w-9 rounded-md bg-muted text-muted-foreground flex items-center justify-center shrink-0"><ListBullets size={18} /></div>
          <div>
            <p className="text-sm font-semibold">{collapsible ? "Open / closed groups" : "Groups stay open"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {collapsible ? "Forget which groups you opened or closed on this device." : "With collapsing off, every group is shown in full with a small heading, like a classic sidebar."}
            </p>
          </div>
        </div>
        {collapsible && <Button variant="outline" size="sm" onClick={resetRemembered} data-testid="menu-reset-groups-btn">Reset</Button>}
      </div>

      <div className="rounded-lg border border-border p-4" data-testid="menu-favourites-card">
        <div className="flex gap-3">
          <div className="h-9 w-9 rounded-md bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0"><Star size={18} weight="fill" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Favourites <span className="text-xs font-normal text-muted-foreground">({favItems.length}/{MAX_FAVOURITES})</span></p>
            <p className="text-xs text-muted-foreground mt-0.5">Hover any menu item and click its star to pin it to the Favourites row at the top of the sidebar. Up to {MAX_FAVOURITES}.</p>
            {favItems.length > 0 ? (
              <ul className="mt-3 space-y-1">
                {favItems.map((f) => {
                  const Icon = f.icon;
                  return (
                    <li key={f.to} className="flex items-center gap-2 rounded-md border border-border/70 px-2.5 py-1.5 text-sm" data-testid={`menu-favourite-${f.label.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and")}`}>
                      {Icon ? <Icon size={16} className="text-muted-foreground" /> : <Star size={16} className="text-muted-foreground" />}
                      <span className="flex-1 truncate">{f.label}</span>
                      <button type="button" onClick={() => unpin(f.to)} className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Unpin" aria-label={`Unpin ${f.label}`} data-testid={`menu-favourite-unpin-${f.label.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and")}`}><X size={13} /></button>
                    </li>
                  );
                })}
              </ul>
            ) : <p className="mt-3 text-xs text-muted-foreground italic" data-testid="menu-favourites-empty">Nothing pinned yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
